import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { CalendarClock, Send, Trash2, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { FaYoutube, FaTiktok, FaInstagram } from "react-icons/fa";

const PLATFORM_ICONS = { YouTube: FaYoutube, TikTok: FaTiktok, Instagram: FaInstagram };

export default function Schedule() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/schedule").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const publish = async (id) => {
    const t = toast.loading("Publishing…");
    try {
      const { data } = await api.post(`/schedule/${id}/publish`);
      toast.success("Published!", {
        id: t,
        action: data.youtube_url ? { label: "Open", onClick: () => window.open(data.youtube_url, "_blank") } : undefined,
      });
      load();
    } catch (err) { toast.error(apiError(err), { id: t }); load(); }
  };

  const remove = async (id) => {
    await api.delete(`/schedule/${id}`);
    load();
  };

  const upcoming = posts.filter((p) => ["scheduled", "publishing", "failed"].includes(p.status));
  const published = posts.filter((p) => p.status === "published");

  return (
    <div className="space-y-8" data-testid="schedule-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Distribution</p>
        <h1 className="font-head text-3xl font-bold tracking-tight">Schedule</h1>
        <p className="text-zinc-400 mt-1 text-sm">Your social queue across YouTube, TikTok & Instagram.</p>
      </div>

      {loading ? (
        <p className="text-zinc-500">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-md p-16 text-center">
          <CalendarClock className="h-10 w-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-2">Nothing scheduled yet.</p>
          <Link to="/library" className="text-blue-400 hover:text-blue-300">Open a video to schedule it →</Link>
        </div>
      ) : (
        <>
          <Section title={`Upcoming (${upcoming.length})`}>
            {upcoming.length === 0 ? <Empty>No upcoming posts.</Empty> : upcoming.map((p) => (
              <PostRow key={p.id} post={p} onPublish={() => publish(p.id)} onDelete={() => remove(p.id)} />
            ))}
          </Section>
          {published.length > 0 && (
            <Section title={`Published (${published.length})`}>
              {published.map((p) => <PostRow key={p.id} post={p} onDelete={() => remove(p.id)} />)}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-head text-lg font-semibold mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-zinc-500 border border-dashed border-white/10 rounded-md p-6 text-center">{children}</p>;
}

function PostRow({ post, onPublish, onDelete }) {
  const date = new Date(post.scheduled_at);
  const isPublished = post.status === "published";
  const isPublishing = post.status === "publishing";
  const isFailed = post.status === "failed";
  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-4 flex items-center gap-4" data-testid={`schedule-post-${post.id}`}>
      <div className="hidden sm:flex flex-col items-center justify-center w-14 h-14 rounded-md bg-[#0A0A0A] border border-white/10 shrink-0">
        <span className="font-head text-lg font-bold leading-none">{date.getDate()}</span>
        <span className="text-[10px] text-zinc-500 uppercase">{date.toLocaleString("en", { month: "short" })}</span>
      </div>
      <div className="min-w-0 flex-1">
        <Link to={`/video/${post.video_id}`} className="font-medium truncate block hover:text-blue-400 transition-colors">{post.video_title}</Link>
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          <div className="flex gap-1.5">
            {post.platforms.map((pl) => {
              const Icon = PLATFORM_ICONS[pl];
              if (!Icon) return null;
              const link = post.channel_links?.[pl];
              return link ? (
                <a
                  key={pl}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open your ${pl} channel`}
                  data-testid={`channel-link-${pl}-${post.id}`}
                  className="text-zinc-400 hover:text-blue-400 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ) : (
                <Icon key={pl} className="h-4 w-4 text-zinc-400" />
              );
            })}
          </div>
          <span className="text-xs text-zinc-500">{date.toLocaleString("en", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</span>
          {isPublished && <span className="text-xs text-green-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Published</span>}
          {isPublishing && <span className="text-xs text-blue-400 inline-flex items-center gap-1" data-testid={`status-publishing-${post.id}`}><Loader2 className="h-3 w-3 animate-spin" /> Publishing…</span>}
          {isFailed && <span className="text-xs text-red-400 inline-flex items-center gap-1" data-testid={`status-failed-${post.id}`} title={post.error || ""}><AlertCircle className="h-3 w-3" /> Failed — retry</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isPublished && !isPublishing && (
          <button onClick={onPublish} data-testid={`publish-btn-${post.id}`} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors">
            <Send className="h-3.5 w-3.5" /> {isFailed ? "Retry" : "Publish now"}
          </button>
        )}
        <button onClick={onDelete} className="h-8 w-8 flex items-center justify-center rounded-md border border-white/10 hover:bg-red-500/10 hover:text-red-400 transition-colors">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
